import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { checkinPhotoUrl, isPhotoFetchable } from '@awwyeah/shared';
import { Avatar } from './components/Avatar';
import { CheckinPhoto } from './components/CheckinPhoto';
import { PlaceRow } from './components/PlacePin';
import { formatClock } from './feed';
import { useMembers } from './members';
import { useCheckins } from './useCheckins';

/**
 * One check-in, opened (design: "one check-in, opened").
 *
 * *"Tapping a photo opens it and nothing else — no next/previous rabbit hole, no 'more
 * from Kora.'"* There is deliberately no swipe gesture, no adjacent-post prefetch and no
 * related content on this screen; the only ways out are Back and Save.
 */
export function CheckinPhotoPage(): JSX.Element {
  const { checkinId } = useParams<{ checkinId: string }>();
  const navigate = useNavigate();
  const { checkins, markSeen, selfId } = useCheckins();
  const members = useMembers();

  const checkin = checkins.find((entry) => entry.id === checkinId);

  // Opening a check-in is about as clear a "seen" as exists.
  useEffect(() => {
    if (checkin && selfId && checkin.authorId !== selfId) markSeen(checkin.id);
  }, [checkin, markSeen, selfId]);

  if (!checkin) {
    return (
      <div className="flex flex-col gap-3 py-16" data-testid="checkin-single">
        <div className="w-12 border-t-2 border-fg" />
        <div className="text-[19px] font-bold tracking-tight text-fg">That one’s not here</div>
        <div className="text-[15px] text-fg-2">It may not have synced to this device yet.</div>
        <button
          type="button"
          onClick={() => navigate('/checkin')}
          className="mt-2 h-12 self-start rounded-md bg-inverse px-5 text-[15px] font-semibold text-inverse-fg"
        >
          Back to the feed
        </button>
      </div>
    );
  }

  const author = members.get(checkin.authorId);
  const savable = checkin.photoRef && isPhotoFetchable(checkin.photoRef);

  return (
    <div className="-mx-4 flex min-h-[70vh] flex-col gap-2 pb-8" data-testid="checkin-single">
      <header className="flex h-14 items-center border-b border-line bg-surface pl-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="flex h-11 w-11 items-center justify-center text-fg-2"
        >
          <ChevronLeft size={20} strokeWidth={1.75} />
        </button>
      </header>

      <div className="flex flex-col gap-2 px-4 pt-2">
        {checkin.photoRef && (
          <CheckinPhoto
            photoRef={checkin.photoRef}
            alt={checkin.text ?? `${author.name}’s photo`}
            className="!aspect-auto max-h-[70vh]"
          />
        )}

        <div className="flex items-center gap-2.5">
          <Avatar member={author} size={24} />
          <span className="flex-1 truncate text-[13px] text-fg-2">
            {[author.name, formatClock(checkin.createdAt), checkin.place?.name]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {savable && checkin.photoRef && (
            <a
              href={checkinPhotoUrl(checkin.photoRef.id)}
              download={`checkin-${checkin.id}.jpg`}
              className="text-[13px] font-semibold text-member-faye"
              data-testid="checkin-save"
            >
              Save
            </a>
          )}
        </div>

        {checkin.text && checkin.photoRef && (
          <p className="m-0 text-base leading-[1.45] text-fg">{checkin.text}</p>
        )}
        {checkin.text && !checkin.photoRef && (
          <p className="m-0 text-[19px] font-medium leading-[1.4] text-fg">{checkin.text}</p>
        )}

        {checkin.place && <PlaceRow place={checkin.place} color={author.color} />}
      </div>
    </div>
  );
}
